import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// 主题色配置 - 与 ThemeSettings 中的 PRESET_THEMES 保持一致
const THEME_COLORS = {
  blue: {
    primary: '#3B82F6',
    primaryDark: '#2563EB',
    primaryLight: '#60A5FA',
    gradientFrom: '#3B82F6',
    gradientTo: '#2563EB',
  },
  purple: {
    primary: '#8B5CF6',
    primaryDark: '#7C3AED',
    primaryLight: '#A78BFA',
    gradientFrom: '#8B5CF6',
    gradientTo: '#7C3AED',
  },
  green: {
    primary: '#10B981',
    primaryDark: '#059669',
    primaryLight: '#34D399',
    gradientFrom: '#10B981',
    gradientTo: '#059669',
  },
  orange: {
    primary: '#F97316',
    primaryDark: '#EA580C',
    primaryLight: '#FB923C',
    gradientFrom: '#F97316',
    gradientTo: '#EA580C',
  },
  pink: {
    primary: '#EC4899',
    primaryDark: '#DB2777',
    primaryLight: '#F472B6',
    gradientFrom: '#EC4899',
    gradientTo: '#DB2777',
  },
  cyan: {
    primary: '#06B6D4',
    primaryDark: '#0891B2',
    primaryLight: '#22D3EE',
    gradientFrom: '#06B6D4',
    gradientTo: '#0891B2',
  },
  red: {
    primary: '#EF4444',
    primaryDark: '#DC2626',
    primaryLight: '#F87171',
    gradientFrom: '#EF4444',
    gradientTo: '#DC2626',
  },
  indigo: {
    primary: '#6366F1',
    primaryDark: '#4F46E5',
    primaryLight: '#818CF8',
    gradientFrom: '#6366F1',
    gradientTo: '#4F46E5',
  },
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return { 
      themeColor: 'blue', 
      colors: THEME_COLORS.blue,
      setThemeColor: () => {},
    };
  }
  return context;
};

export const ThemeProvider = ({ children, initialTheme = 'blue' }) => {
  const [themeColor, setThemeColorState] = useState(initialTheme);
  const colors = THEME_COLORS[themeColor] || THEME_COLORS.blue;

  // 应用主题色到 CSS 变量
  const applyTheme = useCallback((colorId) => {
    const theme = THEME_COLORS[colorId] || THEME_COLORS.blue;
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-dark', theme.primaryDark);
    root.style.setProperty('--theme-primary-light', theme.primaryLight);
    root.style.setProperty('--theme-primary-bg', `${theme.primary}1A`);
    root.style.setProperty('--theme-primary-border', `${theme.primary}4D`);
    root.style.setProperty('--theme-gradient-from', theme.gradientFrom);
    root.style.setProperty('--theme-gradient-to', theme.gradientTo);
  }, []);

  // 包装 setThemeColor 以同时应用主题
  const setThemeColor = useCallback((colorId) => {
    setThemeColorState(colorId);
    applyTheme(colorId);
  }, [applyTheme]);

  // 初始化时应用主题
  useEffect(() => {
    applyTheme(themeColor);
  }, []);

  const value = {
    themeColor,
    setThemeColor,
    colors,
    applyTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export { THEME_COLORS };
export default ThemeContext;
